import React, { useEffect, useState } from 'react'
import { IonIcon } from '@ionic/react'
import { Form, useFormik, FormikProvider, useFormikContext } from 'formik'
import styled from '@emotion/styled'
import { Link, useHistory } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { useSelector, useDispatch } from 'react-redux'
import { arrowForwardOutline, eyeOffOutline, eyeOutline } from 'ionicons/icons'
import { authActions } from './AuthStore'
import { IStateRedux } from '../common/ReduxStore'
import { cursosActions } from '../cursos/CursosStore'
import { Yup } from '../common/Yup'
import { useOpenJivoChat } from '../common/hooks/useJivoApi'
import { AuthPageLayout } from './AuthPageLayout'
import {
  AuthField,
  AuthFormBox,
  AuthPasswordToggle,
  AuthPasswordWrap,
  AuthPrimaryButton,
  AuthSubtitle,
  AuthTitle,
  maskCpf,
} from './AuthForm'

type LoginFormValues = {
  usuario: string
  senha: string
}

const CPF_NOT_FOUND_ERROR = 'Esse CPF não existe'
const PASSWORD_ERRORS = ['Senha incorreta', 'Verifique seu e-mail para cadastrar sua senha!']

export const Login = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const actions = bindActionCreators(authActions, dispatch)
  const isAuthenticated = useSelector((s: IStateRedux) => s.auth.isAuthenticated)
  const request = useSelector((s: IStateRedux) => s.auth.request)
  const cursosUsuario = useSelector((s: IStateRedux) => s.cursos.cursosUsuario)
  const { EsqueceuSenha } = Login

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(cursosActions.getCursosAluno())
    } else {
      dispatch(cursosActions.limparCursosAluno())
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (
      isAuthenticated &&
      cursosUsuario.filter((f) => f.statusDetran !== 'FINALIZADO').length === 1
    ) {
      const curso = cursosUsuario.filter((f) => f.statusDetran !== 'FINALIZADO')[0]
      actions.newTokenMatricula({
        matriculaId: curso.matriculaId,
        callback: () => history.replace(`/curso/${curso.id}/home`),
      })
    } else if (isAuthenticated) {
      history.replace('/seleciona-curso')
    }
  }, [cursosUsuario])

  const formik = useFormik({
    initialValues: { usuario: '', senha: '' },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      usuario: (Yup as any).string().required().cpf(),
      senha: Yup.string().required(),
    }),
    onSubmit: (values) => {
      const cpf = values.usuario
      const senha = values.senha
      actions.login({ cpf, senha })
    },
  })

  const loginError = request.fetching ? '' : request.message
  const cpfLoginError = loginError === CPF_NOT_FOUND_ERROR ? loginError : ''
  const senhaLoginError = PASSWORD_ERRORS.includes(loginError) ? loginError : ''
  const generalLoginError = cpfLoginError || senhaLoginError ? '' : loginError
  const isSubmitDisabled =
    request.fetching || !formik.isValid || !formik.values.usuario || !formik.values.senha

  return (
    <AuthPageLayout>
      <AuthFormBox>
        <AuthTitle>
          <strong>Pronto para continuar?</strong>
        </AuthTitle>
        <AuthSubtitle>
          Digite seus dados abaixo para acessar o seu painel de treinamento.
        </AuthSubtitle>
        <FormikProvider value={formik}>
          <Form noValidate onSubmit={formik.handleSubmit}>
            <CpfField loginError={cpfLoginError} />
            <SenhaField loginError={senhaLoginError} />
            <GeneralLoginError loginError={generalLoginError} />
            <AuthPrimaryButton type="submit" disabled={isSubmitDisabled}>
              {request.fetching ? (
                'ACESSANDO...'
              ) : (
                <>
                  ACESSAR MINHAS AULAS
                  <IonIcon icon={arrowForwardOutline} />
                </>
              )}
            </AuthPrimaryButton>
            <EsqueceuSenha>
              <span>Esqueceu sua senha?</span>{' '}
              <Link to="/recupera-senha">Clique aqui para recuperar.</Link>
            </EsqueceuSenha>
          </Form>
        </FormikProvider>
      </AuthFormBox>
    </AuthPageLayout>
  )
}

function CpfField({ loginError }: { loginError: string }) {
  const { LoginError } = Login
  const formik = useFormikContext<LoginFormValues>()
  const { openJivoChat } = useOpenJivoChat()
  const [showLoginError, setShowLoginError] = useState(false)

  useEffect(() => {
    setShowLoginError(!!loginError)
  }, [loginError])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowLoginError(false)
    formik.setFieldValue('usuario', maskCpf(event.target.value))
  }

  const handleOpenSupport = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    openJivoChat({
      customData: [
        { title: 'Local', content: 'Login' },
        { title: 'Motivo', content: 'Erro de acesso' },
        { title: 'CPF', content: formik.values.usuario },
      ],
    })
  }

  const validationError =
    formik.touched.usuario && formik.errors.usuario ? formik.errors.usuario : ''
  const hasLoginError = showLoginError && !!loginError
  const hasError = !!validationError || hasLoginError

  return (
    <AuthField hasError={hasError}>
      <label htmlFor="usuario">CPF</label>
      <input
        autoFocus
        id="usuario"
        name="usuario"
        type="tel"
        inputMode="numeric"
        maxLength={14}
        placeholder="000.000.000-00"
        value={formik.values.usuario}
        onBlur={formik.handleBlur}
        onChange={handleChange}
        aria-invalid={hasError}
      />
      {validationError ? <p className="field-error">{validationError}</p> : null}
      {!validationError && hasLoginError ? (
        <LoginError role="alert">
          Ops! Não encontramos esse CPF cadastrado. Verifique os números ou{' '}
          <button type="button" onClick={handleOpenSupport}>
            fale com nosso suporte
          </button>{' '}
          para ativar seu acesso.
        </LoginError>
      ) : null}
    </AuthField>
  )
}

function SenhaField({ loginError }: { loginError: string }) {
  const formik = useFormikContext<LoginFormValues>()
  const [showPassword, setShowPassword] = useState(false)
  const [showLoginError, setShowLoginError] = useState(false)

  useEffect(() => {
    setShowLoginError(!!loginError)
  }, [loginError])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowLoginError(false)
    formik.handleChange(event)
  }

  const validationError =
    formik.touched.senha && formik.errors.senha ? formik.errors.senha : ''
  const hasLoginError = showLoginError && !!loginError
  const hasError = !!validationError || hasLoginError

  return (
    <AuthField hasError={hasError}>
      <label htmlFor="senha">Senha</label>
      <AuthPasswordWrap>
        <input
          id="senha"
          name="senha"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••"
          value={formik.values.senha}
          onBlur={formik.handleBlur}
          onChange={handleChange}
          aria-invalid={hasError}
        />
        <AuthPasswordToggle
          type="button"
          onClick={() => setShowPassword((current) => !current)}
          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
        >
          <IonIcon icon={showPassword ? eyeOutline : eyeOffOutline} />
        </AuthPasswordToggle>
      </AuthPasswordWrap>
      {validationError ? <p className="field-error">{validationError}</p> : null}
      {!validationError && hasLoginError ? (
        <p className="field-error" role="alert">
          {loginError}
        </p>
      ) : null}
    </AuthField>
  )
}

function GeneralLoginError({ loginError }: { loginError: string }) {
  const { LoginError } = Login
  const { values } = useFormikContext<LoginFormValues>()
  const [showLoginError, setShowLoginError] = useState(false)

  useEffect(() => {
    setShowLoginError(!!loginError)
  }, [loginError])

  useEffect(() => {
    setShowLoginError(false)
  }, [values.usuario, values.senha])

  return showLoginError && loginError ? (
    <LoginError role="alert">{loginError}</LoginError>
  ) : null
}

Login.LoginError = styled.p`
  margin: 8px 0 0;
  color: #ff0000;
  font-size: 12px;
  line-height: 16px;

  button {
    border: 0;
    padding: 0;
    background: transparent;
    color: #2fa35a;
    font: inherit;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
  }
`

Login.EsqueceuSenha = styled.div`
  margin-top: 24px;
  color: #122a41;
  font-size: 16px;
  line-height: 24px;
  text-align: center;

  span {
    color: #ff6422;
  }

  a {
    color: #122a41;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`
